import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Child, RequiredChecklist } from '../lib/types';
import { useParent } from './ParentContext';

/**
 * Required subjects (#618): the subjects the family's state rule requires, which of the child's own subjects cover
 * each (matched by name, or as the family chose), and the year's hours and lessons for them.
 */
const num = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });

export function RequiredSubjects({ child }: { child: Child }) {
  const { toast } = useParent();
  const [data, setData] = useState<RequiredChecklist | null>(null);
  const [open, setOpen] = useState('');
  const load = useCallback(() => {
    api
      .get<RequiredChecklist | null>(`/api/children/${child.id}/attendance/required-subjects`)
      .then(setData)
      .catch(() => setData(null));
  }, [child.id]);
  useEffect(load, [load]);
  if (!data?.items.length) return null;

  async function save(name: string, subjects: string[] | null) {
    try {
      setData(await api.put<RequiredChecklist>(`/api/children/${child.id}/attendance/required-subjects`, { name, subjects }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  const missing = data.items.filter((i) => !i.hours && !i.lessons).length;

  return (
    <div className="required-subjects stack">
      <div className="row-between wrap">
        <h3>Required subjects</h3>
        <span className={`small ${missing ? 'warn-text' : 'good-text'}`}>{missing ? `${missing} with nothing logged yet` : 'Every required subject has time logged'}</span>
      </div>
      <p className="muted small">
        What {data.state} asks for, matched to {child.name}’s own subjects. Change a match if it’s wrong.
      </p>
      <div className="table-scroll">
        <table className="required-table">
          <thead>
            <tr>
              <th>Required</th>
              <th>Covered by</th>
              <th className="num">Hours</th>
              <th className="num">Lessons</th>
              <th aria-label="Change" />
            </tr>
          </thead>
          <tbody>
            {data.items.map((i) => {
              const empty = !i.hours && !i.lessons;
              return [
                <tr key={i.name} className={empty ? 'empty' : ''}>
                  <th scope="row">{i.name}</th>
                  <td>
                    <span className="chip-list">
                      {i.subjects.map((s) => (
                        <span key={s} className="split-chip">
                          {s}
                        </span>
                      ))}
                      {!i.subjects.length && <span className="warn-text small">Not matched</span>}
                    </span>
                  </td>
                  <td className="num">{i.hours ? `${num(i.hours)} h` : '—'}</td>
                  <td className="num">{i.lessons || '—'}</td>
                  <td className="num">
                    <button type="button" className="btn small" onClick={() => setOpen(open === i.name ? '' : i.name)} aria-expanded={open === i.name}>
                      {open === i.name ? 'Done' : 'Change'}
                    </button>
                  </td>
                </tr>,
                open === i.name && (
                  <tr key={`${i.name}-edit`} className="required-edit">
                    <td colSpan={5}>
                      <div className="stack">
                        <span className="small">Which of {child.name}’s subjects count as {i.name}?</span>
                        <div className="chip-list">
                          {data.childSubjects.map((s) => {
                            const on = i.subjects.includes(s);
                            return (
                              <button
                                key={s}
                                type="button"
                                className={`chip ${on ? 'on' : ''}`}
                                aria-pressed={on}
                                onClick={() => save(i.name, on ? i.subjects.filter((x) => x !== s) : [...i.subjects, s])}
                              >
                                {on ? '✓ ' : ''}
                                {s}
                              </button>
                            );
                          })}
                          {!data.childSubjects.length && <span className="muted small">Add subjects in {child.name}’s Profile → Subjects first.</span>}
                        </div>
                        {!i.auto && (
                          <button type="button" className="link-btn small" onClick={() => save(i.name, null)}>
                            Match by name again
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
      <p className="muted small">Hours come from days split by subject; lessons from lesson plans and the planner.</p>
    </div>
  );
}
